import React, { useState, useEffect } from 'react';

/**
 * VRS (Verbal Rating Scale) Component - eCOA Consortium Compliant
 * 
 * Implements Verbal Rating Scale according to FDA PRO Guidance and eCOA standards:
 * 
 * Unipolar VRS:
 * - Discrete, ordered, uni-directional verbal descriptors
 * - Example: "Not at All", "A Little Bit", "Moderately", "Quite a Bit", "Extremely"
 * 
 * Bipolar VRS (Likert-Type):
 * - Bi-directional with explicit midpoint
 * - Odd number of response categories (allows neutral response)
 * - Example: "Much better", "Better", "No change", "Worse", "Much worse"
 * 
 * eCOA Requirements:
 * - Equidistant formatting (all options appear equidistant)
 * - Consistent hit-spot size regardless of text length
 * - Minimum font size for readability
 * - Response scales rendered consistently across devices
 * 
 * Reference: eCOA Consortium Response Scales Documentation
 */
const VRSScale = ({
  options = [
    { value: 0, label: "Not at All" },
    { value: 1, label: "A Little Bit" },
    { value: 2, label: "Moderately" },
    { value: 3, label: "Quite a Bit" },
    { value: 4, label: "Extremely" }
  ],
  value = null,
  onChange,
  disabled = false,
  orientation = 'vertical', // 'vertical' (recommended) or 'horizontal'
  type = 'unipolar', // 'unipolar' or 'bipolar'
  hideMetadata = false, // Hide eCOA compliance metadata (for preview/participant view)
  spacing = 12, // Spacing between buttons in pixels (default: 12px)
  // Styling customization
  buttonHeight = 48,
  buttonMinWidth = 300,
  fontSize = 14,
  fontWeight = '500',
  borderRadius = 8,
  borderWidth = 2,
  selectedColor = '#2563eb', // blue-600
  unselectedColor = '#ffffff',
  selectedTextColor = '#ffffff',
  unselectedTextColor = '#374151', // gray-700
  borderColor = '#d1d5db', // gray-300
  selectedBorderColor = '#1d4ed8', // blue-700
  hoverColor = '#eff6ff', // blue-50
  hoverBorderColor = '#60a5fa', // blue-400
  className = ""
}) => {
  const [selectedValue, setSelectedValue] = useState(value);

  // Keep internal state in sync with the value prop (prevents stale display on re-visit/resume)
  useEffect(() => {
    setSelectedValue(value);
  }, [value]);

  const handleSelect = (val) => {
    if (disabled) return;
    setSelectedValue(val);
    if (onChange) {
      onChange(val);
    }
  };

  // eCOA Standards: Minimum sizes for touch-friendly interaction
  const MIN_BUTTON_HEIGHT = 48; // px - minimum tap target height
  const MIN_FONT_SIZE = 14; // px - minimum readable font size
  const BUTTON_PADDING = 16; // px - comfortable padding

  // Determine midpoint for bipolar scales
  const midpointIndex = type === 'bipolar' ? Math.floor(options.length / 2) : null;

  if (orientation === 'horizontal') {
    return (
      <div className={`vrs-scale-horizontal flex flex-col items-center w-full ${className}`}>
        <div className="flex flex-wrap items-center justify-center" style={{ gap: `${spacing}px` }}>
          {options.map((option, index) => {
            const isMidpoint = type === 'bipolar' && index === midpointIndex;
            const isSelected = selectedValue === option.value;
            
            return (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                disabled={disabled}
                className={`vrs-button transition-all duration-200 relative shadow-lg ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105'} ${isSelected ? 'scale-105' : ''}`}
                style={{
                  minHeight: `${Math.max(buttonHeight, MIN_BUTTON_HEIGHT)}px`,
                  fontSize: `${Math.max(fontSize, MIN_FONT_SIZE)}px`,
                  fontWeight: fontWeight,
                  border: `${borderWidth}px solid`,
                  borderRadius: `${borderRadius}px`,
                  borderColor: isSelected ? selectedBorderColor : (isMidpoint ? borderColor : borderColor),
                  backgroundColor: isSelected ? selectedColor : (isMidpoint ? '#f9fafb' : unselectedColor),
                  color: isSelected ? selectedTextColor : (isMidpoint ? '#6b7280' : unselectedTextColor),
                  padding: `12px ${BUTTON_PADDING}px`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  flex: '1 1 auto',
                  minWidth: '120px'
                }}
                onMouseEnter={(e) => {
                  if (!disabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = hoverColor;
                    e.currentTarget.style.borderColor = hoverBorderColor;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!disabled && !isSelected) {
                    e.currentTarget.style.backgroundColor = isMidpoint ? '#f9fafb' : unselectedColor;
                    e.currentTarget.style.borderColor = borderColor;
                  }
                }}
                aria-label={option.label}
              >
                <span className="leading-tight text-center">
                  {option.label}
                </span>
                {isMidpoint && type === 'bipolar' && !hideMetadata && (
                  <div className="absolute -top-2 -right-2 w-5 h-5 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                    ⚖
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* eCOA Compliance Notice */}
        {!hideMetadata && process.env.NODE_ENV === 'development' && (
          <div className="mt-3 text-xs text-gray-400 font-mono text-center">
            VRS {type}: {MIN_BUTTON_HEIGHT}px min height • {MIN_FONT_SIZE}px font • Equidistant layout
          </div>
        )}
      </div>
    );
  }

  // Vertical orientation (recommended for VRS)
  return (
    <div className={`vrs-scale-vertical flex flex-col items-stretch w-full max-w-md ${className}`}>
      {options.map((option, index) => {
        const isMidpoint = type === 'bipolar' && index === midpointIndex;
        const isSelected = selectedValue === option.value;
        
        return (
          <button
            key={option.value}
            onClick={() => handleSelect(option.value)}
            disabled={disabled}
            className={`vrs-button transition-all duration-200 relative last:mb-0 shadow-lg ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105'} ${isSelected ? 'scale-105' : ''}`}
            style={{
              minHeight: `${Math.max(buttonHeight, MIN_BUTTON_HEIGHT)}px`,
              height: `${Math.max(buttonHeight, MIN_BUTTON_HEIGHT)}px`,
              minWidth: `${buttonMinWidth}px`,
              fontSize: `${Math.max(fontSize, MIN_FONT_SIZE)}px`,
              fontWeight: fontWeight,
              border: `${borderWidth}px solid`,
              borderRadius: `${borderRadius}px`,
              borderColor: isSelected ? selectedBorderColor : (isMidpoint ? borderColor : borderColor),
              backgroundColor: isSelected ? selectedColor : (isMidpoint ? '#f9fafb' : unselectedColor),
              color: isSelected ? selectedTextColor : (isMidpoint ? '#6b7280' : unselectedTextColor),
              padding: `12px ${BUTTON_PADDING}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              marginBottom: index < options.length - 1 ? `${spacing}px` : '0'
            }}
            onMouseEnter={(e) => {
              if (!disabled && !isSelected) {
                e.currentTarget.style.backgroundColor = hoverColor;
                e.currentTarget.style.borderColor = hoverBorderColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!disabled && !isSelected) {
                e.currentTarget.style.backgroundColor = isMidpoint ? '#f9fafb' : unselectedColor;
                e.currentTarget.style.borderColor = borderColor;
              }
            }}
            aria-label={option.label}
          >
            <span className="leading-tight">
              {option.label}
            </span>
            {isMidpoint && type === 'bipolar' && !hideMetadata && (
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md">
                ⚖
              </div>
            )}
          </button>
        );
      })}

      {/* Selected Value Display (optional, for testing) */}
      {!hideMetadata && selectedValue !== null && process.env.NODE_ENV === 'development' && (
        <div className="mt-3 px-3 py-1 bg-gray-100 border border-gray-300 rounded text-xs text-gray-600 font-mono text-center">
          Selected: {options.find(o => o.value === selectedValue)?.label} (value: {selectedValue})
        </div>
      )}

      {/* eCOA Compliance Notice */}
      {!hideMetadata && process.env.NODE_ENV === 'development' && (
        <div className="mt-3 text-xs text-gray-400 font-mono text-center">
          VRS {type}: {MIN_BUTTON_HEIGHT}px buttons • {MIN_FONT_SIZE}px font • Vertical recommended
        </div>
      )}

      {/* Type-Specific Information */}
      {!hideMetadata && type === 'bipolar' && (
        <div className="mt-2 text-xs text-gray-500 italic text-center">
          Bipolar scale: {options.length} options (odd number allows neutral midpoint)
        </div>
      )}
    </div>
  );
};

export default VRSScale;
